import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Receipt, Wallet, Users, PieChart, Sparkles,
  Boxes, ArrowLeftRight, Settings as SettingsIcon, LogOut, Bell, Coins, Contact, HandCoins, FileInput,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { MoneyModeToggle } from "@/components/MoneyInput";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, key: "home" },
  { to: "/transactions", label: "Transaksi", icon: Receipt, key: "trx" },
  { to: "/import-gopay", label: "Impor Mutasi", icon: FileInput, key: "import" },
  { to: "/apps", label: "Aplikasi", icon: Boxes, key: "apps" },
  { to: "/cash", label: "Kas", icon: Coins, key: "cash" },
  { to: "/expenses", label: "Pengeluaran", icon: Wallet, key: "expenses" },
  { to: "/debts", label: "Utang", icon: Users, key: "debts" },
  { to: "/titipan", label: "Titipan", icon: HandCoins, key: "titipan" },
  { to: "/customers", label: "Pelanggan", icon: Contact, key: "customers" },
  { to: "/reconciliation", label: "Rekonsiliasi", icon: ArrowLeftRight, key: "reconciliation" },
  { to: "/reports", label: "Laporan", icon: PieChart, key: "reports" },
  { to: "/ai", label: "AI Insight", icon: Sparkles, key: "ai" },
  { to: "/settings", label: "Pengaturan", icon: SettingsIcon, key: "settings" },
];

const MOBILE_NAV = NAV.filter((n) => ["home", "trx", "debts", "reports", "ai"].includes(n.key));

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications", { params: { unread_only: true, limit: 20 } })
      .then((r) => setUnread(r.data?.length || 0)).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground grid place-items-center font-bold">
              LT
            </div>
            <div>
              <p className="font-bold text-base tracking-tight">Loket Transaksi</p>
              <p className="text-xs text-muted-foreground">Pusat Kendali Usaha</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-accent/40">
            <div className="h-9 w-9 rounded-full bg-primary/20 grid place-items-center font-semibold text-primary text-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
            </div>
            <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Sticky header */}
        <header className="sticky top-0 z-30 border-b border-border glass">
          <div className="px-4 lg:px-8 h-14 flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold text-xs">
                LT
              </div>
              <span className="font-bold tracking-tight">Loket</span>
            </div>
            <div className="hidden lg:block text-sm text-muted-foreground">
              {new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
            <div className="flex items-center gap-2">
              <MoneyModeToggle />
              <NavLink to="/notifications" data-testid="nav-notifications" className="relative p-2 rounded-lg hover:bg-accent">
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 min-w-[20px] p-0 grid place-items-center text-[10px] rounded-full">
                    {unread}
                  </Badge>
                )}
              </NavLink>
              <Button data-testid="mobile-logout-btn" variant="ghost" size="icon" className="lg:hidden" onClick={logout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <div className="flex-1 px-3 lg:px-4 py-5 lg:py-8 pb-24 lg:pb-8 w-full">
          <Outlet />
        </div>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border glass">
          <div className="grid grid-cols-5">
            {MOBILE_NAV.map((n) => (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.to === "/"}
                data-testid={`bottomnav-${n.key}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2.5 gap-0.5 ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`
                }
              >
                <n.icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{n.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </main>
    </div>
  );
}
